import { use } from "react";

import { useDbClient, useLiveQuery } from "@tanstack/react-db";

import { buildHref } from "@softmax/web-addresses";

import { useTrackedSignOut } from "@softmax/web-features/telemetry";

import {
  analyticsEvents,
  analyticsSources,
} from "@softmax/web-features/telemetry/events";

import {
  QueryViewerContext,
  collectionError,
  refreshCollections,
} from "@softmax/web-features/data";

import { myUsageStatusCollection } from "@/features/usage/records";

import { ApiError } from "@softmax/observatory-client/transport";

import { accountSettingsHref } from "@softmax/web-features/site";

import joinedStyles from "./account-pill.module.css";

import { type MenuProps, Item, RetryRow } from "./pill-menu-items";

function AccountRows({ data, member, onNavigate }: MenuProps) {
  const styles = joinedStyles;
  const signOut = useTrackedSignOut({
    event: analyticsEvents.siteNavAccountMenuSignOutClicked,
    properties: { source: analyticsSources.siteNavAccountMenu },
  });
  const links = member
    ? [
        { label: "Account settings", href: accountSettingsHref },
        { label: "Manage Coworlds", href: buildHref({ name: "coworlds" }) },
      ]
    : [
        {
          label: "Sign in",
          href: buildHref({
            name: "signIn",
            search: new URLSearchParams({ redirect_url: data.pathname }),
          }),
        },
      ];
  return (
    <>
      {member && <CreditBalance />}
      {links.map((link) => (
        <Item
          key={link.label}
          link={link}
          styles={styles}
          onNavigate={onNavigate}
        />
      ))}
      {data.account.isError && (
        <RetryRow
          message="Could not load account details."
          label="Retry account details"
          retry={() => void data.account.refetch()}
          styles={styles}
        />
      )}
      {data.playersError && (
        <RetryRow
          message="Could not load your player."
          label="Retry player"
          retry={() => void data.refreshPlayers()}
          styles={styles}
        />
      )}
      {member && (
        <li>
          <button
            type="button"
            className={styles.item}
            disabled={signOut.isPending}
            onClick={() => signOut.mutate()}
          >
            {signOut.isPending ? "Signing out…" : "Sign out"}
          </button>
          {signOut.isError && (
            <output className={styles.heading}>
              Could not sign out. Please try again.
            </output>
          )}
        </li>
      )}
    </>
  );
}

export function SiteHeaderAccountMenu(props: MenuProps) {
  return (
    <ul className={joinedStyles.menu} aria-label="Account">
      <AccountRows {...props} />
    </ul>
  );
}

function CreditBalance() {
  const viewerId = use(QueryViewerContext);
  const dbClient = useDbClient();
  const descriptor = myUsageStatusCollection({
    viewerId,
    elevatedPrivileges: false,
  });
  const query = useLiveQuery((q) => q.from({ status: descriptor }).findOne());
  const error = collectionError(query.health);
  const unavailable =
    error instanceof ApiError && (error.status === 401 || error.status === 403);
  const balance = unavailable ? undefined : query.data?.credits.balance_credits;
  let label = "Loading credits…";

  if (balance !== undefined)
    label = `${balance.toLocaleString("en-US")} credits left`;
  else if (error) label = "Credits unavailable";

  return (
    <li className={joinedStyles.credits}>
      {label}
      {error && (
        <button
          type="button"
          className={joinedStyles.item}
          onClick={() => {
            void refreshCollections({
              signal:
                dbClient.requireDependency<AbortController>("lifetime").signal,
              queries: [query.collection],
              refresh: () =>
                dbClient
                  .collection(descriptor)
                  .utils.refetch({ throwOnError: true }),
            });
          }}
        >
          Retry credits
        </button>
      )}
    </li>
  );
}
